import React, { useState } from 'react';
import { sendToZohoWithRetry, getUTMParams } from '../utils/webhook';

/**
 * EmailCapture Component
 * Lead capture form that appears after all questions are answered
 * Validates all fields before allowing submission
 * Sends data to Zoho Flow webhook before showing results
 * 
 * @param {function} onSubmit - Callback with form data when submitted
 * @param {function} onBack - Callback for back button
 * @param {object} answers - Quiz answers (for webhook)
 * @param {function} getTimeToComplete - Function to get completion time
 */
export default function EmailCapture({ onSubmit, onBack, answers, getTimeToComplete }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    companyWebsite: ''
  });
  
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  
  // Validation rules
  const validate = (field, value) => {
    switch (field) {
      case 'firstName':
        return value.trim().length < 2 ? 'Please enter your first name' : '';
      
      case 'lastName':
        return value.trim().length < 2 ? 'Please enter your last name' : '';
      
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return !emailRegex.test(value) ? 'Please enter a valid email address' : '';
      
      case 'companyName':
        return value.trim().length < 2 ? 'Please enter your company name' : '';
      
      case 'companyWebsite':
        const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
        const normalizedValue = value.trim().toLowerCase();
        if (!normalizedValue) return 'Please enter your company website';
        // Add https:// if no protocol specified
        const valueWithProtocol = normalizedValue.startsWith('http') 
          ? normalizedValue 
          : `https://${normalizedValue}`;
        return !urlRegex.test(valueWithProtocol) ? 'Please enter a valid website URL' : '';
      
      default:
        return '';
    }
  };
  
  // Handle field change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (touched[name]) {
      const error = validate(name, value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };
  
  // Handle field blur (mark as touched)
  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validate(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };
  
  // Check if form is valid
  const isFormValid = () => {
    const allFieldsFilled = Object.values(formData).every(val => val.trim().length > 0);
    const noErrors = Object.keys(formData).every(field => !validate(field, formData[field]));
    return allFieldsFilled && noErrors;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    const newErrors = {};
    Object.keys(formData).forEach(field => {
      const error = validate(field, formData[field]);
      if (error) newErrors[field] = error;
    });
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched({
        firstName: true,
        lastName: true,
        email: true,
        companyName: true,
        companyWebsite: true
      });
      return;
    }
    
    // Normalize website URL and combine first/last name
    const normalizedData = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      fullName: `${formData.firstName.trim()} ${formData.lastName.trim()}`, // Combined for compatibility
      email: formData.email.trim(),
      companyName: formData.companyName.trim(),
      companyWebsite: formData.companyWebsite.trim().startsWith('http')
        ? formData.companyWebsite.trim()
        : `https://${formData.companyWebsite.trim()}`
    };
    
    // Send to Zoho Flow webhook (don't block on failure)
    const utmParams = getUTMParams();
    const metadata = {
      timeToComplete: getTimeToComplete ? getTimeToComplete() : 0,
      ...utmParams,
    };
    
    // Send webhook with retry, but don't wait for it or block results
    sendToZohoWithRetry(answers, normalizedData, metadata, 1)
      .then(success => {
        if (success) {
          console.log('[EmailCapture] Webhook sent successfully');
        } else {
          console.warn('[EmailCapture] Webhook failed, but showing results anyway');
        }
      });
    
    // Show results immediately (don't wait for webhook)
    onSubmit(normalizedData);
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center p-3 sm:p-4">
      <div className="max-w-lg md:max-w-xl lg:max-w-2xl w-full">
        <div className="card">
          {/* Header */}
          <div className="text-center mb-4 sm:mb-6 md:mb-8">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-3">
              You're done! Let's see your results.
            </h2>
            <p className="text-gray-300 text-sm sm:text-base md:text-lg">
              Enter your details to unlock your Pipeline Health Report:
            </p>
          </div>
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            {/* Name Fields - Side by Side */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {/* First Name */}
              <div>
                <label htmlFor="firstName" className="block text-xs sm:text-sm font-medium text-gray-300 mb-1 sm:mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`input-field ${touched.firstName && errors.firstName ? 'input-error' : ''}`}
                  placeholder="John"
                  required
                />
                {touched.firstName && errors.firstName && (
                  <p className="text-leak-red text-xs sm:text-sm mt-1">{errors.firstName}</p>
                )}
              </div>
              
              {/* Last Name */}
              <div>
                <label htmlFor="lastName" className="block text-xs sm:text-sm font-medium text-gray-300 mb-1 sm:mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`input-field ${touched.lastName && errors.lastName ? 'input-error' : ''}`}
                  placeholder="Smith"
                  required
                />
                {touched.lastName && errors.lastName && (
                  <p className="text-leak-red text-xs sm:text-sm mt-1">{errors.lastName}</p>
                )}
              </div>
            </div>
            
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-300 mb-1 sm:mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`input-field ${touched.email && errors.email ? 'input-error' : ''}`}
                placeholder="john@company.com"
                required
              />
              {touched.email && errors.email && (
                <p className="text-leak-red text-xs sm:text-sm mt-1">{errors.email}</p>
              )}
            </div>
            
            {/* Company Name */}
            <div>
              <label htmlFor="companyName" className="block text-xs sm:text-sm font-medium text-gray-300 mb-1 sm:mb-2">
                Company Name *
              </label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`input-field ${touched.companyName && errors.companyName ? 'input-error' : ''}`}
                placeholder="Acme Corp"
                required
              />
              {touched.companyName && errors.companyName && (
                <p className="text-leak-red text-xs sm:text-sm mt-1">{errors.companyName}</p>
              )}
            </div>
            
            {/* Company Website */}
            <div>
              <label htmlFor="companyWebsite" className="block text-xs sm:text-sm font-medium text-gray-300 mb-1 sm:mb-2">
                Company Website *
              </label>
              <input
                type="text"
                id="companyWebsite"
                name="companyWebsite"
                value={formData.companyWebsite}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`input-field ${touched.companyWebsite && errors.companyWebsite ? 'input-error' : ''}`}
                placeholder="acmecorp.com"
                required
              />
              {touched.companyWebsite && errors.companyWebsite && (
                <p className="text-leak-red text-xs sm:text-sm mt-1">{errors.companyWebsite}</p>
              )}
            </div>
            
            {/* Benefits */}
            <div className="bg-navy-dark/50 rounded-xl p-3 sm:p-4 my-3 sm:my-4 md:my-6 border border-gray-600/20">
              <p className="text-xs sm:text-sm text-gray-300 mb-1.5 sm:mb-2">We'll send you:</p>
              <ul className="space-y-0.5 sm:space-y-1 text-xs sm:text-sm text-gray-400">
                <li className="flex items-start">
                  <span className="text-thryv-green mr-2">✓</span>
                  Your complete pipeline assessment
                </li>
                <li className="flex items-start">
                  <span className="text-thryv-green mr-2">✓</span>
                  Specific recommendations for your biggest gaps
                </li>
                <li className="flex items-start">
                  <span className="text-thryv-green mr-2">✓</span>
                  Actionable next steps based on your score
                </li>
              </ul>
            </div>
            
            {/* Navigation Buttons */}
            <div className="flex items-center justify-between gap-3 sm:gap-4 pt-2 sm:pt-4">
              <button
                type="button"
                onClick={onBack}
                className="btn-secondary"
              >
                ← Back
              </button>
              
              <button
                type="submit"
                disabled={!isFormValid()}
                className={`
                  btn-primary flex-1
                  ${!isFormValid() ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                VIEW MY RESULTS →
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

